import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import {
  ACCURACY_OPTIONS,
  GEO_SETTINGS_DEFAULTS,
  getGeoSettings,
  updateGeoSettings,
  type GeoSettings,
  type GeoSettingsPatch,
  type LocationAccuracy,
} from '@/api/geo_settings'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * /admin/geo-settings — параметры гео-стрима инспекторов.
 *
 * Источник данных: единственная строка `public.geo_settings` (id=1). Read
 * через RPC `get_geo_settings`, write через RPC `update_geo_settings(jsonb)`
 * под `is_admin()` (RLS на запись закрыт). Дефолты — `GEO_SETTINGS_DEFAULTS`,
 * совпадают с DEFAULT в миграции.
 *
 * Применение на устройствах: мобильное приложение читает настройки при
 * `init` сервиса и при `app_state=resumed`. Изменения подхватываются на
 * следующем заходе app в foreground (не пушится realtime).
 */
export function GeoSettingsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['geo-settings'],
    queryFn: getGeoSettings,
    staleTime: 30_000,
  })

  const [draft, setDraft] = useState<GeoSettingsPatch>({})
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Сбрасываем draft при подмене подложки.
  useEffect(() => {
    if (data) setDraft({})
  }, [data])

  const mut = useMutation({
    mutationFn: (patch: GeoSettingsPatch) => updateGeoSettings(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['geo-settings'] })
      setDraft({})
      setErrorMsg(null)
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  const merged = useMemo<GeoSettingsPatch | null>(() => {
    if (!data) return null
    return { ...data, ...draft }
  }, [data, draft])

  const isDirty = Object.keys(draft).length > 0

  function update<K extends keyof GeoSettingsPatch>(
    key: K,
    value: GeoSettingsPatch[K],
  ) {
    setDraft((prev) => {
      const next = { ...prev, [key]: value }
      // Если значение совпало с серверным — выкидываем из draft.
      if (data && next[key] === (data as GeoSettings)[key]) {
        delete next[key]
      }
      return next
    })
  }

  function resetDefaults() {
    setDraft(GEO_SETTINGS_DEFAULTS)
  }

  function discard() {
    setDraft({})
    setErrorMsg(null)
  }

  function save() {
    if (!isDirty) return
    mut.mutate(draft)
  }

  return (
    <div>
      <PageHeader
        title={t('settings.geo.title')}
        subtitle={t('settings.geo.subtitle')}
      />

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Banner kind="error" text={t('settings.error', { message: (error as Error).message })} />
      ) : !data || !merged ? (
        <Banner kind="muted" text={t('settings.notLoaded')} />
      ) : (
        <div className="flex flex-col gap-6">
          <Section title={t('settings.geo.idle.title')} hint={t('settings.geo.idle.hint')}>
            <ParamGrid>
              <AccuracyField
                label={t('settings.geo.field.iosAccuracy')}
                value={merged.idle_ios_accuracy as LocationAccuracy}
                onChange={(v) => update('idle_ios_accuracy', v)}
              />
              <NumberField
                label={t('settings.geo.field.iosDistanceFilter')}
                value={merged.idle_ios_distance_filter_m ?? 0}
                min={0}
                max={10000}
                onChange={(v) => update('idle_ios_distance_filter_m', v)}
              />
              <AccuracyField
                label={t('settings.geo.field.androidAccuracy')}
                value={merged.idle_android_accuracy as LocationAccuracy}
                onChange={(v) => update('idle_android_accuracy', v)}
              />
              <NumberField
                label={t('settings.geo.field.androidInterval')}
                value={merged.idle_android_interval_s ?? 0}
                min={5}
                max={3600}
                onChange={(v) => update('idle_android_interval_s', v)}
              />
            </ParamGrid>
          </Section>

          <Section title={t('settings.geo.active.title')} hint={t('settings.geo.active.hint')}>
            <ParamGrid>
              <AccuracyField
                label={t('settings.geo.field.iosAccuracy')}
                value={merged.active_ios_accuracy as LocationAccuracy}
                onChange={(v) => update('active_ios_accuracy', v)}
              />
              <NumberField
                label={t('settings.geo.field.iosDistanceFilter')}
                value={merged.active_ios_distance_filter_m ?? 0}
                min={0}
                max={10000}
                onChange={(v) => update('active_ios_distance_filter_m', v)}
              />
              <AccuracyField
                label={t('settings.geo.field.androidAccuracy')}
                value={merged.active_android_accuracy as LocationAccuracy}
                onChange={(v) => update('active_android_accuracy', v)}
              />
              <NumberField
                label={t('settings.geo.field.androidInterval')}
                value={merged.active_android_interval_s ?? 0}
                min={5}
                max={3600}
                onChange={(v) => update('active_android_interval_s', v)}
              />
            </ParamGrid>
          </Section>

          <Section
            title={t('settings.geo.watchdog.title')}
            hint={t('settings.geo.watchdog.hint')}
          >
            <div className="max-w-xs">
              <NumberField
                label={t('settings.geo.watchdog.threshold')}
                value={merged.watchdog_silence_minutes ?? 10}
                min={1}
                max={1440}
                onChange={(v) => update('watchdog_silence_minutes', v)}
              />
            </div>
          </Section>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={!isDirty || mut.isPending}
              className="rounded bg-[color:var(--stryzh-color-accent-orange)] px-4 py-2 text-sm font-semibold text-black transition disabled:opacity-40"
            >
              {mut.isPending ? t('settings.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={discard}
              disabled={!isDirty || mut.isPending}
              className="rounded border border-[color:var(--stryzh-color-border-default)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/5 disabled:opacity-40"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={resetDefaults}
              disabled={mut.isPending}
              className="rounded border border-[color:var(--stryzh-color-border-default)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/5 disabled:opacity-40"
              title={t('settings.geo.resetDefaultsTitle')}
            >
              {t('settings.geo.resetDefaults')}
            </button>
            <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('settings.geo.lastChange', {
                when: formatDistanceToNow(new Date(data.updated_at), {
                  locale: dateFnsLocale(),
                  addSuffix: true,
                }),
              })}
            </div>
          </div>

          {errorMsg ? <Banner kind="error" text={errorMsg} /> : null}
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
      <div className="mb-3">
        <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {title}
        </div>
        {hint ? (
          <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {hint}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function ParamGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {children}
    </div>
  )
}

function AccuracyField({
  label,
  value,
  onChange,
}: {
  label: string
  value: LocationAccuracy
  onChange: (v: LocationAccuracy) => void
}) {
  const opt = ACCURACY_OPTIONS.find((o) => o.value === value)
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as LocationAccuracy)}
        className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)]"
      >
        {ACCURACY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {opt ? (
        <span className="text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
          {opt.hint}
        </span>
      ) : null}
    </label>
  )
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  onChange: (v: number) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)))
        }}
        className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)]"
      />
      <span className="text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
        {min}—{max}
      </span>
    </label>
  )
}

function Banner({
  kind,
  text,
}: {
  kind: 'error' | 'muted'
  text: string
}) {
  const cls =
    kind === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
